// UI strings + category labels + selector labels + result paths + section text
// Languages with full UI translations: 12 codes.
// Product content (long descriptions, features) lives in productContent.js (en + ru full).

export const supportedLanguages = [
  { code: "en", name: "English", native: "English", dir: "ltr", flag: "EN" },
  { code: "ru", name: "Russian", native: "Русский", dir: "ltr", flag: "RU" },
  { code: "ar", name: "Arabic", native: "العربية", dir: "rtl", flag: "AR" },
  { code: "hi", name: "Hindi", native: "हिन्दी", dir: "ltr", flag: "HI" },
  { code: "pcm", name: "Nigerian English", native: "Naija", dir: "ltr", flag: "NG" },
  { code: "pt-BR", name: "Portuguese BR", native: "Português BR", dir: "ltr", flag: "BR" },
  { code: "es", name: "Spanish", native: "Español", dir: "ltr", flag: "ES" },
  { code: "fr", name: "French", native: "Français", dir: "ltr", flag: "FR" },
  { code: "id", name: "Indonesian", native: "Bahasa Indonesia", dir: "ltr", flag: "ID" },
  { code: "tr", name: "Turkish", native: "Türkçe", dir: "ltr", flag: "TR" },
  { code: "vi", name: "Vietnamese", native: "Tiếng Việt", dir: "ltr", flag: "VI" },
  { code: "ur", name: "Urdu", native: "اردو", dir: "rtl", flag: "PK" },
];

const en = {
  // Brand
  brandShort: "MarketEdge Lab",
  brandFull: "MarketEdge Access Lab",
  // Header
  navAccesses: "Accesses",
  navHelp: "Help Me Choose",
  navCodes: "Access Codes",
  navReviews: "Reviews",
  navHow: "How It Works",
  navFAQ: "FAQ",
  ctaRequestOnboarding: "Request Onboarding",
  ctaAskTelegram: "Ask in Telegram",
  // Hero
  heroEyebrow: "Private Educational Access Ecosystem",
  heroTitle: "Choose Your Market Learning Access",
  heroSub: "Explore guided learning paths, technical study modules, session-based tools, analytics dashboards, scenario simulations, personal learning formats, access codes, educational videos and private onboarding in one premium multilingual interface.",
  heroCta1: "Explore Accesses",
  heroCta2: "Help Me Choose",
  heroCta3: "Request Private Onboarding",
  trust1: "No registration required",
  trust2: "Educational demos included",
  trust3: "Private onboarding",
  trust4: "Beginner to advanced paths",
  trust5: "Risk-awareness included",
  coachTitle: "Learning Coach Online",
  coachText: "Need help choosing the right access path? I can guide you through the available modules and onboarding options.",
  // Disclaimer
  disclaimer: "For educational purposes only — not financial advice. Market activity involves risk. No guaranteed outcomes.",
  // Progress
  progressViewing: "Viewing",
  progressOf: "of",
  progressLabel: "Private Access",
  // Featured
  featuredTitle: "Featured Learning Accesses",
  featuredSub: "Start with the most popular paths or explore the full ecosystem below.",
  // Gallery
  galleryTitle: "Full Access Gallery",
  gallerySub: "Eighteen access formats. Every card is a different format, level and learning environment.",
  // Search & filters
  searchPlaceholder: "Search access, module, market style, or strategy...",
  filterAll: "All",
  filterBeginner: "Beginner",
  filterIntermediate: "Intermediate",
  filterAdvanced: "Advanced",
  filterPro: "Pro",
  filterManual: "Manual Study",
  filterSimulation: "Scenario Simulation",
  filterMirror: "Mirror Study",
  filterSession: "Session-Based",
  filterMt5: "MT5-Style",
  filterMiniApp: "Telegram Mini-App",
  filterEducation: "Education",
  filterVideo: "Video Demo",
  filterPersonal: "Personal Support",
  filterGuided: "Guided Learning",
  filterAnalytics: "Advanced Analytics",
  filterCode: "Access Code",
  noResults: "Not sure what to search for? Message support and we'll help you choose.",
  // Card
  cardOpenDetails: "Open Details",
  cardWatchDemo: "Watch Demo",
  cardRequestAccess: "Request Access",
  cardCompare: "Compare",
  cardAddToCompare: "Add to Compare",
  cardRemove: "Remove",
  cardLevel: "Level",
  cardFormat: "Format",
  cardEnvironment: "Environment",
  cardBestFor: "Best For",
  cardKeyFeatures: "Key Features",
  cardHowItWorks: "How It Works",
  videoText: "Watch the educational walkthrough",
  videoSub: "See how this access format works before requesting onboarding.",
  videoSoon: "Educational walkthrough coming soon. Ask support for a guided explanation.",
  // Coach selector
  selTitle: "Not sure which access fits you?",
  selSub: "Choose your current level or goal, and we'll show the most suitable learning path.",
  selOptBeginner: "I am completely new",
  selOptManual: "I already study manually",
  selOptLessManual: "I want less manual work",
  selOptSession: "I want session-based tools",
  selOptMirror: "I want mirror-style observation",
  selOptPersonal: "I want personal 1-on-1 help",
  selOptPro: "I want advanced/pro tools",
  selOptEducation: "I want to learn the logic",
  selOptMt5: "I want MT5-style education",
  selOptFlagship: "I want the strongest flagship software",
  selRecommended: "Recommended for you",
  resBeginner: "This path is built for users who are starting from zero and want structure, simple explanations, support and tools that help them understand the process step by step.",
  resManual: "This path is built for users who want to stay in control, understand market logic and use structured confirmation before making independent decisions elsewhere.",
  resLessManual: "This path is built for users who want tools that reduce manual work, organize analysis and explain settings more clearly.",
  resSession: "This path is built for users who focus on session behavior, volatility rhythm and flexible market observation.",
  resMirror: "This path explains account-level observation, allocation and limit settings as educational concepts.",
  resPersonal: "This path is built for users who want direct explanations, guided review and a more personal educational format.",
  resPro: "This path is built for users who already understand the basics and want advanced logic, strategy selection, risk settings, MT5-style modules or personal-level education.",
  resEducation: "This path focuses on understanding the why — confluence, indicator logic, terminology and market structure.",
  // Access code
  codeTitle: "Learning Access Code",
  codeSub: "Reveal your private code and send it to onboarding to unlock the matching learning route.",
  codeBody: "Scratch the protected area, copy the revealed code and send it to Telegram onboarding. The access code is used only for educational module routing and private onboarding.",
  codeReveal: "Reveal Access Code",
  codeRevealHint: "Scratch to reveal",
  codeCopy: "Copy Code",
  codeCopied: "Copied!",
  codeHowToUse: "How to Use",
  codeStep1: "Reveal the protected code.",
  codeStep2: "Copy the code.",
  codeStep3: "Open private Telegram onboarding.",
  codeStep4: "Send the code to support.",
  codeStep5: "Support will explain which learning section it unlocks.",
  codeStep6: "Choose the access path that fits your level.",
  codeDisclaimer: "Access codes unlock educational content and onboarding routes only. They do not provide financial benefits, financial advice, execution or guaranteed outcomes.",
  codeSendCode: "Send Code to Onboarding",
  // How it works
  howTitle: "How the Access Ecosystem Works",
  howSub: "Every access path has a different purpose. This section helps users understand the difference before choosing.",
  how1Title: "What is an educational access?",
  how1Body: "An educational access is a specific group, module, app, software preview or personal format designed to help users study the market in a more structured way.",
  how2Title: "What is a guided group?",
  how2Body: "A guided group gives scheduled educational sessions, market commentary and support. It is usually easier for beginners because they can follow a clear structure.",
  how3Title: "What is a session-based module?",
  how3Body: "A session-based module helps users study volatility rhythm, market activity and technical behavior during different time periods.",
  how4Title: "What is scenario simulation?",
  how4Body: "Scenario simulation helps users review examples in a controlled educational environment without executing anything.",
  how5Title: "What is mirror study?",
  how5Body: "Mirror study explains account-level observation concepts, allocation examples and limit settings in an educational way.",
  how6Title: "What is manual analysis?",
  how6Body: "Manual analysis tools provide explanations, technical context, indicators and confirmation concepts, but the final decision always remains with the user outside this website.",
  how7Title: "What is an access code?",
  how7Body: "An access code is sent to private onboarding to unlock educational modules or learning routes.",
  // Comparison
  cmpTitle: "Compare Access Paths",
  cmpSub: "Pick up to three accesses and review them side by side.",
  cmpEmpty: "Add cards to compare. Up to three at a time.",
  cmpClear: "Clear comparison",
  cmpName: "Access Name",
  cmpBestFor: "Best For",
  cmpLevel: "User Level",
  cmpEnv: "Environment",
  cmpFormat: "Format",
  cmpManual: "Manual Control",
  cmpBeginner: "Beginner Friendly",
  cmpVideo: "Video Demo",
  cmpSupport: "Support Included",
  cmpEducation: "Education Included",
  cmpRisk: "Risk Settings",
  cmpPlatform: "Platform Type",
  yes: "Yes",
  no: "No",
  // Reviews
  rvTitle: "Community Stories",
  rvSub: "Example-style community feedback based on common user experiences.",
  rvDisclaimer: "These are educational examples — not financial performance claims.",
  // FAQ
  faqTitle: "Frequently Asked Questions",
  faqSub: "Short answers to the most common questions about the access ecosystem.",
  faq1Q: "Is MarketEdge Lab a financial platform?",
  faq1A: "No. MarketEdge Lab is an educational showroom and analytics learning interface. It does not execute actions, manage funds or provide financial advice.",
  faq2Q: "Does the website guarantee outcomes?",
  faq2A: "No. All content is educational only. Market activity involves risk and no tool can guarantee outcomes.",
  faq3Q: "Are access codes financial benefits?",
  faq3A: "No. Access codes only unlock educational content, dashboard previews or onboarding routes.",
  faq4Q: "Can I connect an account here?",
  faq4A: "No. This website does not connect accounts, execute actions or manage user funds.",
  faq5Q: "What can I learn here?",
  faq5A: "Users can study market structure, indicators, session behavior, volatility, scenario logic, risk-awareness concepts and available learning paths.",
  faq6Q: "What is confluence?",
  faq6A: "Confluence means looking at several technical conditions together. It is educational context, not a prediction or recommendation.",
  faq7Q: "How do I choose the right access?",
  faq7A: "Use the selector or message private onboarding. Support will help you choose based on your level and goal.",
  faq8Q: "Is this financial advice?",
  faq8A: "No. All content is educational only.",
  // Support bubble
  supTitle: "Still not sure which access fits you?",
  supText: "Message me directly and I'll help you choose the most suitable path.",
  // Footer
  footerDesc: "A premium multilingual educational access showroom created to help users explore guided groups, analytics modules, scenario simulations, access codes, personal learning formats and private onboarding before choosing the path that fits their level.",
  footerLegal: "All dashboard values, examples, scenarios, access codes and module previews shown on this website are simulated or educational examples only. They should not be interpreted as financial advice, direct instructions, performance claims or future-outcome predictions.",
  footerLinkAccess: "Accesses",
  footerLinkHelp: "Help Me Choose",
  footerLinkCodes: "Access Codes",
  footerLinkReviews: "Reviews",
  footerLinkSupport: "Support",
  footerLinkFAQ: "FAQ",
  footerRights: "All rights reserved.",
  // Mobile sticky
  stickyText: "Private educational access",
  // Sound
  soundOn: "Sound On",
  soundOff: "Sound Off",
  // Categories
  catGuided: "Guided Learning",
  catMirror: "Mirror / Observation",
  catManual: "Manual Analysis",
  catSession: "Session Systems",
  catAnalytics: "Advanced Analytics",
  catSupport: "Access / Support",
  // Levels
  levelBeginner: "Beginner",
  levelIntermediate: "Intermediate",
  levelAdvanced: "Advanced",
  levelPro: "Pro",
  levelAll: "All levels",
  // misc
  close: "Close",
  back: "Back",
  next: "Next",
  // language selector
  langTitle: "Select Language",
  langSearch: "Search language",
};

const ru = {
  brandShort: "MarketEdge Lab",
  brandFull: "MarketEdge Access Lab",
  navAccesses: "Доступы",
  navHelp: "Помочь с выбором",
  navCodes: "Коды доступа",
  navReviews: "Отзывы",
  navHow: "Как это работает",
  navFAQ: "FAQ",
  ctaRequestOnboarding: "Приватный онбординг",
  ctaAskTelegram: "Открыть в Telegram",
  heroEyebrow: "Приватная экосистема образовательных доступов",
  heroTitle: "Выбери свой образовательный доступ к рынку",
  heroSub: "Изучай направляемые пути, технические модули, сессионные инструменты, аналитические дашборды, симуляции сценариев, личные форматы, коды доступа, образовательные видео и приватный онбординг в одном премиум-интерфейсе.",
  heroCta1: "Смотреть доступы",
  heroCta2: "Помочь с выбором",
  heroCta3: "Запросить приватный онбординг",
  trust1: "Регистрация не нужна",
  trust2: "Образовательные демо включены",
  trust3: "Приватный онбординг",
  trust4: "От новичка до Pro",
  trust5: "Риск-осознанность включена",
  coachTitle: "Coach на связи",
  coachText: "Нужна помощь с выбором маршрута? Я расскажу про доступные модули и форматы онбординга.",
  disclaimer: "Только в образовательных целях — не финансовая рекомендация. Активность на рынке связана с риском. Никаких гарантий результата.",
  progressViewing: "Сейчас",
  progressOf: "из",
  progressLabel: "Приватный доступ",
  featuredTitle: "Избранные образовательные доступы",
  featuredSub: "Начни с самых востребованных маршрутов или изучи всю экосистему ниже.",
  galleryTitle: "Полная галерея доступов",
  gallerySub: "Восемнадцать форматов доступа. Каждая карточка — отдельный формат, уровень и среда обучения.",
  searchPlaceholder: "Найти доступ, модуль, рынок или стратегию...",
  filterAll: "Все",
  filterBeginner: "Новичок",
  filterIntermediate: "Средний",
  filterAdvanced: "Продвинутый",
  filterPro: "Pro",
  filterManual: "Ручное изучение",
  filterSimulation: "Симуляция сценариев",
  filterMirror: "Mirror Study",
  filterSession: "Сессионные",
  filterMt5: "MT5-стиль",
  filterMiniApp: "Telegram mini-app",
  filterEducation: "Образование",
  filterVideo: "Видео-демо",
  filterPersonal: "Личное сопровождение",
  filterGuided: "Guided",
  filterAnalytics: "Продвинутая аналитика",
  filterCode: "Код доступа",
  noResults: "Не уверен, что искать? Напиши в поддержку — поможем выбрать.",
  cardOpenDetails: "Открыть детали",
  cardWatchDemo: "Смотреть демо",
  cardRequestAccess: "Получить доступ",
  cardCompare: "Сравнить",
  cardAddToCompare: "Добавить к сравнению",
  cardRemove: "Убрать",
  cardLevel: "Уровень",
  cardFormat: "Формат",
  cardEnvironment: "Среда",
  cardBestFor: "Подходит для",
  cardKeyFeatures: "Ключевые возможности",
  cardHowItWorks: "Как это работает",
  videoText: "Образовательный обзор",
  videoSub: "Посмотри, как работает формат, прежде чем запрашивать онбординг.",
  videoSoon: "Образовательное видео скоро появится. Можно запросить разбор у поддержки.",
  selTitle: "Не уверен, какой доступ тебе подходит?",
  selSub: "Выбери свой уровень или цель — покажем самый подходящий маршрут.",
  selOptBeginner: "Я полный новичок",
  selOptManual: "Уже изучаю вручную",
  selOptLessManual: "Хочу меньше ручной работы",
  selOptSession: "Хочу сессионные инструменты",
  selOptMirror: "Хочу Mirror-наблюдение",
  selOptPersonal: "Хочу личное 1-на-1",
  selOptPro: "Хочу продвинутые/Pro инструменты",
  selOptEducation: "Хочу понять логику",
  selOptMt5: "Хочу MT5-обучение",
  selOptFlagship: "Хочу самый сильный флагман",
  selRecommended: "Рекомендовано для тебя",
  resBeginner: "Маршрут для тех, кто начинает с нуля и хочет структуру, простые объяснения, поддержку и инструменты, которые помогают шаг за шагом.",
  resManual: "Маршрут для тех, кто хочет оставаться в контроле, понимать логику рынка и использовать структурированное подтверждение до самостоятельных решений вне сайта.",
  resLessManual: "Маршрут для тех, кому нужны инструменты, снижающие ручную работу, организующие анализ и более чётко объясняющие настройки.",
  resSession: "Маршрут для тех, кто фокусируется на поведении сессий, ритме волатильности и гибком наблюдении.",
  resMirror: "Этот маршрут объясняет наблюдение на уровне аккаунта, аллокацию и лимиты как образовательные концепции.",
  resPersonal: "Маршрут для тех, кому нужны прямые объяснения, направляемый разбор и более личный формат.",
  resPro: "Маршрут для тех, кто уже понимает основы и хочет продвинутую логику, выбор стратегии, риск-настройки, MT5-модули или личное обучение.",
  resEducation: "Маршрут об осмысленном «почему»: confluence, логика индикаторов, терминология и структура рынка.",
  codeTitle: "Код доступа к обучению",
  codeSub: "Открой свой приватный код и отправь его в онбординг, чтобы разблокировать соответствующий маршрут.",
  codeBody: "Сотри защищённую область, скопируй открытый код и отправь его в Telegram-онбординг. Код используется только для маршрутизации образовательных модулей и приватного онбординга.",
  codeReveal: "Открыть код доступа",
  codeRevealHint: "Сотри, чтобы открыть",
  codeCopy: "Скопировать код",
  codeCopied: "Скопировано!",
  codeHowToUse: "Как использовать",
  codeStep1: "Открой защищённый код.",
  codeStep2: "Скопируй код.",
  codeStep3: "Открой приватный Telegram-онбординг.",
  codeStep4: "Отправь код в поддержку.",
  codeStep5: "Поддержка объяснит, какой раздел открывает код.",
  codeStep6: "Выбери маршрут под свой уровень.",
  codeDisclaimer: "Коды доступа открывают только образовательный контент и маршруты онбординга. Они не дают финансовой выгоды, финансовых советов, исполнения или гарантированных результатов.",
  codeSendCode: "Отправить код в онбординг",
  howTitle: "Как работает экосистема доступов",
  howSub: "У каждого пути своя цель. Этот раздел поможет понять разницу до выбора.",
  how1Title: "Что такое образовательный доступ?",
  how1Body: "Это конкретная группа, модуль, приложение, превью софта или личный формат, помогающий изучать рынок более структурированно.",
  how2Title: "Что такое направляемая группа?",
  how2Body: "Это расписание сессий, комментарии к рынку и поддержка. Обычно проще для новичков из-за чёткой структуры.",
  how3Title: "Что такое сессионный модуль?",
  how3Body: "Помогает изучать ритм волатильности, активность рынка и техническое поведение в разные периоды.",
  how4Title: "Что такое симуляция сценариев?",
  how4Body: "Помогает разбирать примеры в контролируемой образовательной среде без исполнения.",
  how5Title: "Что такое Mirror Study?",
  how5Body: "Объясняет наблюдение на уровне аккаунта, примеры аллокации и настройки лимитов как образовательные концепции.",
  how6Title: "Что такое ручной анализ?",
  how6Body: "Это инструменты с объяснениями, техническим контекстом, индикаторами и подтверждением. Решение всегда остаётся за пользователем вне этого сайта.",
  how7Title: "Что такое код доступа?",
  how7Body: "Код отправляется в приватный онбординг, чтобы открыть образовательные модули или маршруты.",
  cmpTitle: "Сравнить доступы",
  cmpSub: "Выбери до трёх доступов и сравни их рядом.",
  cmpEmpty: "Добавь карточки для сравнения. До трёх одновременно.",
  cmpClear: "Очистить сравнение",
  cmpName: "Название",
  cmpBestFor: "Подходит",
  cmpLevel: "Уровень",
  cmpEnv: "Среда",
  cmpFormat: "Формат",
  cmpManual: "Ручной контроль",
  cmpBeginner: "Для новичков",
  cmpVideo: "Видео-демо",
  cmpSupport: "Поддержка",
  cmpEducation: "Образование",
  cmpRisk: "Риск-настройки",
  cmpPlatform: "Тип платформы",
  yes: "Да",
  no: "Нет",
  rvTitle: "Истории сообщества",
  rvSub: "Примеры обратной связи на основе типичного опыта пользователей.",
  rvDisclaimer: "Это образовательные примеры — не заявления о финансовых результатах.",
  faqTitle: "Часто задаваемые вопросы",
  faqSub: "Короткие ответы на самые частые вопросы об экосистеме.",
  faq1Q: "MarketEdge Lab — это финансовая платформа?",
  faq1A: "Нет. MarketEdge Lab — это образовательный showroom и аналитический интерфейс. Сайт не исполняет действий, не управляет средствами и не даёт финансовых советов.",
  faq2Q: "Сайт гарантирует результаты?",
  faq2A: "Нет. Контент только образовательный. Активность на рынке связана с риском, и ни один инструмент не гарантирует результата.",
  faq3Q: "Коды доступа — это финансовая выгода?",
  faq3A: "Нет. Коды открывают только образовательный контент, превью дашбордов или маршруты онбординга.",
  faq4Q: "Можно ли подключить здесь аккаунт?",
  faq4A: "Нет. Сайт не подключает аккаунты, не исполняет действий и не управляет средствами.",
  faq5Q: "Что я могу здесь изучить?",
  faq5A: "Структуру рынка, индикаторы, поведение сессий, волатильность, логику сценариев, концепции риск-осознанности и доступные маршруты.",
  faq6Q: "Что такое confluence?",
  faq6A: "Это рассмотрение нескольких технических условий вместе. Это образовательный контекст, а не прогноз или рекомендация.",
  faq7Q: "Как выбрать правильный доступ?",
  faq7A: "Используй селектор или напиши в приватный онбординг. Поддержка поможет выбрать по уровню и цели.",
  faq8Q: "Это финансовый совет?",
  faq8A: "Нет. Весь контент только образовательный.",
  supTitle: "Не уверен, какой доступ тебе подходит?",
  supText: "Напиши напрямую — помогу выбрать самый подходящий маршрут.",
  footerDesc: "Премиальный многоязычный образовательный showroom доступов: исследуй направляемые группы, аналитические модули, симуляции сценариев, коды, личные форматы и приватный онбординг до выбора маршрута под свой уровень.",
  footerLegal: "Все значения дашбордов, примеры, сценарии, коды и превью модулей на сайте — симулированные или образовательные. Их нельзя интерпретировать как финансовый совет, прямые инструкции, заявления о результатах или прогнозы.",
  footerLinkAccess: "Доступы",
  footerLinkHelp: "Помочь с выбором",
  footerLinkCodes: "Коды доступа",
  footerLinkReviews: "Отзывы",
  footerLinkSupport: "Поддержка",
  footerLinkFAQ: "FAQ",
  footerRights: "Все права защищены.",
  stickyText: "Приватный образовательный доступ",
  soundOn: "Звук вкл.",
  soundOff: "Звук выкл.",
  catGuided: "Guided Learning",
  catMirror: "Mirror / Наблюдение",
  catManual: "Ручной анализ",
  catSession: "Сессионные системы",
  catAnalytics: "Продвинутая аналитика",
  catSupport: "Доступ / Поддержка",
  levelBeginner: "Новичок",
  levelIntermediate: "Средний",
  levelAdvanced: "Продвинутый",
  levelPro: "Pro",
  levelAll: "Все уровни",
  close: "Закрыть",
  back: "Назад",
  next: "Дальше",
  langTitle: "Выбери язык",
  langSearch: "Поиск языка",
};

// Compact translations for other 10 languages — UI strings + categories + selector + filters fully translated.
const ar = {
  ...en,
  brandShort: "MarketEdge Lab",
  brandFull: "مختبر الوصول MarketEdge",
  navAccesses: "الوصول",
  navHelp: "ساعدني في الاختيار",
  navCodes: "رموز الوصول",
  navReviews: "آراء المجتمع",
  navHow: "كيف يعمل",
  navFAQ: "أسئلة شائعة",
  ctaRequestOnboarding: "طلب إعداد خاص",
  ctaAskTelegram: "اسأل عبر تيليجرام",
  heroEyebrow: "نظام وصول تعليمي خاص",
  heroTitle: "اختر مسار التعلم الخاص بك للسوق",
  heroSub: "استكشف مسارات تعلم موجهة، وحدات دراسة فنية، أدوات قائمة على الجلسات، لوحات تحليلية، محاكاة سيناريوهات، تنسيقات تعلم شخصية، رموز وصول، فيديوهات تعليمية وإعداد خاص في واجهة بريميوم متعددة اللغات.",
  heroCta1: "استكشف الوصول",
  heroCta2: "ساعدني في الاختيار",
  heroCta3: "اطلب إعدادًا خاصًا",
  trust1: "بدون تسجيل",
  trust2: "عروض تعليمية مدمجة",
  trust3: "إعداد خاص",
  trust4: "من المبتدئ إلى المتقدم",
  trust5: "وعي بالمخاطر مدمج",
  coachTitle: "المدرس متصل",
  coachText: "هل تحتاج مساعدة في اختيار المسار؟ أرشدك خلال الوحدات وخيارات الإعداد.",
  disclaimer: "لأغراض تعليمية فقط — ليست نصيحة مالية. النشاط في السوق يتضمن مخاطر. لا توجد نتائج مضمونة.",
  featuredTitle: "أبرز المسارات",
  featuredSub: "ابدأ بأكثر المسارات شعبية أو استكشف النظام أدناه.",
  galleryTitle: "معرض الوصول الكامل",
  gallerySub: "ثمانية عشر تنسيقًا. كل بطاقة بمستوى وبيئة مختلفة.",
  searchPlaceholder: "ابحث عن وصول، وحدة، نمط سوق أو استراتيجية...",
  noResults: "لست متأكدًا؟ راسل الدعم وسنساعدك في الاختيار.",
  cardOpenDetails: "فتح التفاصيل",
  cardWatchDemo: "شاهد العرض",
  cardRequestAccess: "اطلب الوصول",
  cardCompare: "مقارنة",
  cardAddToCompare: "أضف للمقارنة",
  cardLevel: "المستوى",
  cardFormat: "الصيغة",
  cardEnvironment: "البيئة",
  cardBestFor: "الأنسب لـ",
  cardKeyFeatures: "أهم الميزات",
  cardHowItWorks: "كيف يعمل",
  videoText: "شاهد الجولة التعليمية",
  videoSub: "اطلع على كيفية عمل التنسيق قبل طلب الإعداد.",
  selTitle: "غير متأكد من الوصول المناسب؟",
  selSub: "اختر مستواك الحالي أو هدفك، وسنعرض المسار الأنسب.",
  selOptBeginner: "أنا مبتدئ تمامًا",
  selOptManual: "أدرس يدويًا بالفعل",
  selOptLessManual: "أريد عملًا يدويًا أقل",
  selOptSession: "أريد أدوات قائمة على الجلسات",
  selOptMirror: "أريد ملاحظة بأسلوب Mirror",
  selOptPersonal: "أريد مساعدة شخصية 1-1",
  selOptPro: "أريد أدوات احترافية",
  selOptEducation: "أريد فهم المنطق",
  selOptMt5: "أريد تعليمًا بأسلوب MT5",
  selOptFlagship: "أريد البرنامج الرائد الأقوى",
  selRecommended: "موصى به لك",
  codeTitle: "رمز الوصول التعليمي",
  codeSub: "اكشف رمزك الخاص وأرسله إلى الإعداد لفتح المسار المطابق.",
  codeReveal: "اكشف رمز الوصول",
  codeCopy: "نسخ الرمز",
  codeCopied: "تم النسخ!",
  codeHowToUse: "طريقة الاستخدام",
  howTitle: "كيف يعمل نظام الوصول",
  howSub: "كل مسار له هدف مختلف. هذا القسم يساعدك على فهم الفرق قبل الاختيار.",
  cmpTitle: "قارن مسارات الوصول",
  cmpSub: "اختر حتى ثلاثة وراجعها جنبًا إلى جنب.",
  yes: "نعم",
  no: "لا",
  rvTitle: "قصص المجتمع",
  rvSub: "ملاحظات تعليمية مبنية على تجارب شائعة.",
  faqTitle: "أسئلة شائعة",
  faqSub: "إجابات قصيرة على الأسئلة الأكثر شيوعًا.",
  supTitle: "ما زلت غير متأكد؟",
  supText: "راسلني مباشرة وسأساعدك في اختيار المسار الأنسب.",
  footerDesc: "معرض تعليمي بريميوم متعدد اللغات لاستكشاف المجموعات الموجهة، الوحدات التحليلية، محاكاة السيناريوهات، رموز الوصول والإعداد الخاص.",
  footerRights: "جميع الحقوق محفوظة.",
  stickyText: "وصول تعليمي خاص",
  langTitle: "اختر اللغة",
};

const hi = {
  ...en,
  navAccesses: "एक्सेस",
  navHelp: "मेरी मदद करें",
  navCodes: "एक्सेस कोड",
  navReviews: "समीक्षाएँ",
  navHow: "यह कैसे काम करता है",
  navFAQ: "FAQ",
  ctaRequestOnboarding: "निजी ऑनबोर्डिंग",
  ctaAskTelegram: "टेलीग्राम पर पूछें",
  heroEyebrow: "निजी शैक्षिक एक्सेस इकोसिस्टम",
  heroTitle: "अपनी मार्केट लर्निंग एक्सेस चुनें",
  heroSub: "एक प्रीमियम बहुभाषी इंटरफ़ेस में मार्गदर्शित पथ, तकनीकी मॉड्यूल, सत्र-आधारित उपकरण, एनालिटिक्स डैशबोर्ड, परिदृश्य सिमुलेशन, व्यक्तिगत प्रारूप, एक्सेस कोड, शैक्षिक वीडियो और निजी ऑनबोर्डिंग एक्सप्लोर करें।",
  heroCta1: "एक्सेस देखें",
  heroCta2: "मेरी मदद करें",
  heroCta3: "निजी ऑनबोर्डिंग",
  trust1: "रजिस्ट्रेशन की ज़रूरत नहीं",
  trust2: "शैक्षिक डेमो शामिल",
  trust3: "निजी ऑनबोर्डिंग",
  trust4: "नए से उन्नत तक",
  trust5: "जोखिम जागरूकता शामिल",
  coachTitle: "कोच ऑनलाइन",
  coachText: "मार्ग चुनने में मदद चाहिए? मैं उपलब्ध मॉड्यूल और ऑनबोर्डिंग विकल्पों में आपका मार्गदर्शन करूँगा।",
  disclaimer: "केवल शैक्षिक उद्देश्यों के लिए — वित्तीय सलाह नहीं। बाज़ार गतिविधि में जोखिम शामिल है। कोई गारंटीकृत परिणाम नहीं।",
  featuredTitle: "विशेष शैक्षिक एक्सेस",
  galleryTitle: "पूर्ण एक्सेस गैलरी",
  selTitle: "तय नहीं कौन-सा एक्सेस फिट है?",
  selSub: "अपना वर्तमान स्तर चुनें — हम सबसे उपयुक्त मार्ग दिखाएँगे।",
  codeTitle: "लर्निंग एक्सेस कोड",
  codeReveal: "कोड प्रकट करें",
  codeCopy: "कोड कॉपी करें",
  codeCopied: "कॉपी हो गया!",
  cmpTitle: "एक्सेस की तुलना करें",
  yes: "हाँ", no: "नहीं",
  rvTitle: "समुदाय की कहानियाँ",
  faqTitle: "अक्सर पूछे जाने वाले प्रश्न",
  supTitle: "अभी भी अनिश्चित?",
  footerRights: "सर्वाधिकार सुरक्षित।",
  stickyText: "निजी शैक्षिक एक्सेस",
};

const pcm = {
  ...en,
  navAccesses: "Access",
  navHelp: "Help Me Pick",
  navCodes: "Access Code",
  navHow: "How E Dey Work",
  navFAQ: "Question Dem",
  ctaRequestOnboarding: "Start Onboarding",
  ctaAskTelegram: "Ask For Telegram",
  heroEyebrow: "Private Learning Access System",
  heroTitle: "Pick The Market Learning Access Wey Match You",
  heroSub: "Check guided paths, technical modules, session tools, analytics dashboards, scenario simulations, personal formats, access codes, learning videos and private onboarding for one premium multilingual interface.",
  heroCta1: "Check Access",
  heroCta2: "Help Me Pick",
  heroCta3: "Start Private Onboarding",
  trust1: "No registration needed",
  trust2: "Learning demo dey",
  trust3: "Private onboarding",
  trust4: "Beginner reach Pro",
  trust5: "Risk awareness dey",
  coachTitle: "Coach Dey Online",
  coachText: "You need help wey go fit you? I go guide you through the modules and onboarding options.",
  disclaimer: "Na for learning only — no be financial advice. Market get risk. No guaranteed result.",
  selTitle: "You no sure which one go fit you?",
  codeReveal: "Open Access Code",
  codeCopy: "Copy Code",
  yes: "Yes", no: "No",
  stickyText: "Private learning access",
};

const ptBR = {
  ...en,
  navAccesses: "Acessos",
  navHelp: "Me Ajude a Escolher",
  navCodes: "Códigos",
  navReviews: "Depoimentos",
  navHow: "Como Funciona",
  navFAQ: "Perguntas",
  ctaRequestOnboarding: "Onboarding Privado",
  ctaAskTelegram: "Falar no Telegram",
  heroEyebrow: "Ecossistema Educacional Privado",
  heroTitle: "Escolha Seu Acesso de Aprendizado de Mercado",
  heroSub: "Explore caminhos guiados, módulos técnicos, ferramentas baseadas em sessões, painéis analíticos, simulações de cenários, formatos pessoais, códigos de acesso, vídeos educacionais e onboarding privado em uma interface premium multilíngue.",
  heroCta1: "Ver Acessos",
  heroCta2: "Me Ajude a Escolher",
  heroCta3: "Onboarding Privado",
  trust1: "Sem registro",
  trust2: "Demos educacionais",
  trust3: "Onboarding privado",
  trust4: "Iniciante até Pro",
  trust5: "Consciência de risco incluída",
  coachTitle: "Coach Online",
  coachText: "Precisa de ajuda escolhendo o caminho? Vou guiar você pelos módulos e opções de onboarding.",
  disclaimer: "Apenas para fins educacionais — não é conselho financeiro. Atividade de mercado envolve risco. Sem resultados garantidos.",
  featuredTitle: "Acessos em Destaque",
  galleryTitle: "Galeria Completa",
  selTitle: "Não sabe qual acesso combina?",
  codeTitle: "Código de Acesso",
  codeReveal: "Revelar Código",
  codeCopy: "Copiar",
  codeCopied: "Copiado!",
  cmpTitle: "Comparar Acessos",
  yes: "Sim", no: "Não",
  rvTitle: "Histórias da Comunidade",
  faqTitle: "Perguntas Frequentes",
  supTitle: "Ainda em dúvida?",
  footerRights: "Todos os direitos reservados.",
  stickyText: "Acesso educacional privado",
};

const es = {
  ...en,
  navAccesses: "Accesos",
  navHelp: "Ayúdame a Elegir",
  navCodes: "Códigos",
  navReviews: "Reseñas",
  navHow: "Cómo Funciona",
  navFAQ: "Preguntas",
  ctaRequestOnboarding: "Onboarding Privado",
  ctaAskTelegram: "Pregúntame en Telegram",
  heroEyebrow: "Ecosistema Educacional Privado",
  heroTitle: "Elige Tu Acceso de Aprendizaje de Mercado",
  heroSub: "Explora rutas guiadas, módulos técnicos, herramientas por sesiones, paneles analíticos, simulaciones, formatos personales, códigos de acceso, videos educativos y onboarding privado en una interfaz premium multilingüe.",
  heroCta1: "Ver Accesos",
  heroCta2: "Ayúdame a Elegir",
  heroCta3: "Onboarding Privado",
  trust1: "Sin registro",
  trust2: "Demos incluidos",
  trust3: "Onboarding privado",
  trust4: "Principiante a Pro",
  trust5: "Consciencia de riesgo",
  coachTitle: "Coach en línea",
  coachText: "¿Necesitas ayuda eligiendo? Te guío entre módulos y opciones de onboarding.",
  disclaimer: "Solo con fines educativos — no es asesoramiento financiero. La actividad de mercado implica riesgo. Sin resultados garantizados.",
  featuredTitle: "Accesos Destacados",
  galleryTitle: "Galería Completa",
  selTitle: "¿No sabes cuál te queda?",
  codeReveal: "Revelar Código",
  codeCopy: "Copiar",
  cmpTitle: "Comparar Accesos",
  yes: "Sí", no: "No",
  stickyText: "Acceso educacional privado",
};

const fr = {
  ...en,
  navAccesses: "Accès",
  navHelp: "Aidez-moi à choisir",
  navCodes: "Codes",
  navReviews: "Avis",
  navHow: "Comment ça marche",
  navFAQ: "FAQ",
  ctaRequestOnboarding: "Onboarding Privé",
  ctaAskTelegram: "Demander sur Telegram",
  heroEyebrow: "Écosystème Éducatif Privé",
  heroTitle: "Choisissez Votre Accès d'Apprentissage du Marché",
  heroSub: "Explorez des parcours guidés, modules techniques, outils par sessions, tableaux analytiques, simulations, formats personnels, codes d'accès, vidéos éducatives et onboarding privé dans une interface premium multilingue.",
  heroCta1: "Voir les Accès",
  heroCta2: "Aidez-moi à choisir",
  heroCta3: "Onboarding Privé",
  trust1: "Aucune inscription",
  trust2: "Démos incluses",
  trust3: "Onboarding privé",
  trust4: "Débutant à Pro",
  trust5: "Sensibilisation au risque",
  coachTitle: "Coach en ligne",
  coachText: "Besoin d'aide pour choisir ? Je vous guide dans les modules et options d'onboarding.",
  disclaimer: "À des fins éducatives uniquement — pas un conseil financier. L'activité de marché comporte des risques. Aucun résultat garanti.",
  selTitle: "Pas sûr du parcours qui vous convient ?",
  codeReveal: "Révéler le Code",
  codeCopy: "Copier",
  yes: "Oui", no: "Non",
  stickyText: "Accès éducatif privé",
};

const id = {
  ...en,
  navAccesses: "Akses",
  navHelp: "Bantu Saya Memilih",
  navCodes: "Kode Akses",
  navReviews: "Ulasan",
  navHow: "Cara Kerja",
  ctaRequestOnboarding: "Onboarding Privat",
  ctaAskTelegram: "Tanya di Telegram",
  heroEyebrow: "Ekosistem Edukasi Privat",
  heroTitle: "Pilih Akses Pembelajaran Pasar Anda",
  heroSub: "Jelajahi jalur terpandu, modul teknis, alat berbasis sesi, dasbor analitik, simulasi skenario, format pribadi, kode akses, video edukasi, dan onboarding privat dalam satu antarmuka premium multibahasa.",
  heroCta1: "Lihat Akses",
  heroCta2: "Bantu Saya Memilih",
  heroCta3: "Onboarding Privat",
  trust1: "Tanpa registrasi",
  trust2: "Demo edukasi",
  trust3: "Onboarding privat",
  trust4: "Pemula sampai Pro",
  trust5: "Kesadaran risiko",
  coachTitle: "Pelatih Online",
  disclaimer: "Hanya untuk tujuan edukasi — bukan saran finansial. Aktivitas pasar mengandung risiko. Tidak ada hasil yang dijamin.",
  selTitle: "Tidak yakin akses mana yang cocok?",
  codeReveal: "Ungkap Kode",
  codeCopy: "Salin",
  yes: "Ya", no: "Tidak",
  stickyText: "Akses edukasi privat",
};

const tr = {
  ...en,
  navAccesses: "Erişimler",
  navHelp: "Seçmeme Yardım Et",
  navCodes: "Erişim Kodları",
  navReviews: "Yorumlar",
  navHow: "Nasıl Çalışır",
  ctaRequestOnboarding: "Özel Onboarding",
  ctaAskTelegram: "Telegram'da Sor",
  heroEyebrow: "Özel Eğitim Erişim Ekosistemi",
  heroTitle: "Piyasa Öğrenme Erişimini Seç",
  heroSub: "Premium çok dilli arayüzde rehberli yollar, teknik modüller, seans araçları, analitik panolar, senaryo simülasyonları, kişisel formatlar, erişim kodları, eğitim videoları ve özel onboarding'i keşfet.",
  heroCta1: "Erişimleri Gör",
  heroCta2: "Seçmeme Yardım Et",
  heroCta3: "Özel Onboarding",
  trust1: "Kayıt gerekmez",
  trust2: "Eğitim demoları dahil",
  trust3: "Özel onboarding",
  trust4: "Başlangıçtan Pro'ya",
  trust5: "Risk farkındalığı dahil",
  coachTitle: "Koç Çevrimiçi",
  disclaimer: "Yalnızca eğitim amaçlıdır — finansal tavsiye değildir. Piyasa aktivitesi risk içerir. Garantili sonuç yoktur.",
  selTitle: "Hangi erişim sana uygun emin değil misin?",
  codeReveal: "Kodu Aç",
  codeCopy: "Kopyala",
  yes: "Evet", no: "Hayır",
  stickyText: "Özel eğitim erişimi",
};

const vi = {
  ...en,
  navAccesses: "Truy Cập",
  navHelp: "Giúp Tôi Chọn",
  navCodes: "Mã Truy Cập",
  navReviews: "Đánh Giá",
  navHow: "Cách Hoạt Động",
  ctaRequestOnboarding: "Onboarding Riêng",
  ctaAskTelegram: "Hỏi Telegram",
  heroEyebrow: "Hệ Sinh Thái Học Tập Riêng",
  heroTitle: "Chọn Truy Cập Học Tập Thị Trường Của Bạn",
  heroSub: "Khám phá các lộ trình có hướng dẫn, mô-đun kỹ thuật, công cụ theo phiên, bảng phân tích, mô phỏng kịch bản, định dạng cá nhân, mã truy cập, video giáo dục và onboarding riêng trong một giao diện cao cấp đa ngôn ngữ.",
  heroCta1: "Xem Truy Cập",
  heroCta2: "Giúp Tôi Chọn",
  heroCta3: "Onboarding Riêng",
  trust1: "Không cần đăng ký",
  trust2: "Demo giáo dục",
  trust3: "Onboarding riêng",
  trust4: "Người mới đến Pro",
  trust5: "Nhận thức rủi ro",
  coachTitle: "Huấn luyện viên trực tuyến",
  disclaimer: "Chỉ dành cho mục đích giáo dục — không phải lời khuyên tài chính. Hoạt động thị trường có rủi ro. Không có kết quả đảm bảo.",
  selTitle: "Không chắc truy cập nào phù hợp?",
  codeReveal: "Mở Mã",
  codeCopy: "Sao chép",
  yes: "Có", no: "Không",
  stickyText: "Truy cập học tập riêng",
};

const ur = {
  ...en,
  navAccesses: "رسائی",
  navHelp: "میری مدد کریں",
  navCodes: "رسائی کوڈز",
  navReviews: "آراء",
  navHow: "یہ کیسے کام کرتا ہے",
  ctaRequestOnboarding: "نجی آن بورڈنگ",
  ctaAskTelegram: "ٹیلیگرام پر پوچھیں",
  heroEyebrow: "نجی تعلیمی رسائی نظام",
  heroTitle: "اپنی مارکیٹ لرننگ رسائی منتخب کریں",
  heroSub: "ایک پریمیم کثیراللسانی انٹرفیس میں رہنمائی شدہ راستے، تکنیکی ماڈیولز، سیشن ٹولز، تجزیاتی ڈیش بورڈز، منظر نامہ سیمولیشنز، ذاتی فارمیٹس، رسائی کوڈز، تعلیمی ویڈیوز اور نجی آن بورڈنگ کا جائزہ لیں۔",
  heroCta1: "رسائی دیکھیں",
  heroCta2: "میری مدد کریں",
  heroCta3: "نجی آن بورڈنگ",
  trust1: "رجسٹریشن کی ضرورت نہیں",
  trust2: "تعلیمی ڈیمو شامل",
  trust3: "نجی آن بورڈنگ",
  trust4: "ابتدائی سے پرو تک",
  trust5: "خطرے کی آگاہی شامل",
  coachTitle: "کوچ آن لائن",
  disclaimer: "صرف تعلیمی مقاصد کے لیے — مالی مشورہ نہیں ہے۔ مارکیٹ کی سرگرمی میں خطرہ ہے۔ کوئی ضمانت شدہ نتائج نہیں۔",
  selTitle: "کون سی رسائی آپ کے لیے مناسب ہے یقین نہیں؟",
  codeReveal: "کوڈ ظاہر کریں",
  codeCopy: "کاپی کریں",
  yes: "ہاں", no: "نہیں",
  stickyText: "نجی تعلیمی رسائی",
};

export const translations = {
  en,
  ru,
  ar,
  hi,
  pcm,
  "pt-BR": ptBR,
  es,
  fr,
  id,
  tr,
  vi,
  ur,
};

export const t = (lang, key) => {
  const dict = translations[lang] || translations.en;
  return dict[key] ?? translations.en[key] ?? key;
};
